import dayjs from 'dayjs'
import 'dayjs/locale/ar'
import 'dayjs/locale/en'

import { NEGATIVE_TRIP_STATUSES, PROGRESS_TRIP_STATUSES } from '@/src/constants/trips'
import { TripStatusTone } from '@/src/types/trips'

export const getStatusTone = (status?: string): TripStatusTone => {
    const normalized = status?.toLowerCase().trim() || ''
    if (NEGATIVE_TRIP_STATUSES.includes(normalized)) return 'error'
    if (PROGRESS_TRIP_STATUSES.includes(normalized)) return 'warning'
    return normalized ? 'success' : 'default'
}

interface TripDateParts {
    dateDay: string
    dateMonth: string
    dateLabel: string
}

export interface TripTitleParts {
    fullTitle: string
    isRoundTrip: boolean
    origin?: string
    destination?: string
}

export const formatTripDateParts = (
    travelDate: string | null | undefined,
    locale: string,
    fallbackLabel: string
): TripDateParts => {
    if (!travelDate) {
        return { dateDay: '--', dateMonth: '', dateLabel: fallbackLabel }
    }
    try {
        const parsed = dayjs(travelDate.replace('+0000', 'Z'))
        if (!parsed.isValid()) {
            return { dateDay: '--', dateMonth: '', dateLabel: fallbackLabel }
        }
        const localized = parsed.locale(locale)
        return {
            dateDay: localized.format('DD'),
            dateMonth: localized.format('MMM'),
            dateLabel: localized.format('DD MMM YYYY')
        }
    } catch {
        return { dateDay: '--', dateMonth: '', dateLabel: fallbackLabel }
    }
}

export const formatTripTitle = (
    trip: { tripName?: string; tripId: string },
    untitledFallback: string
): string => {
    return formatTripTitleParts(trip, untitledFallback).fullTitle
}

export const formatTripTitleParts = (
    trip: { tripName?: string; tripId: string },
    untitledFallback: string
): TripTitleParts => {
    if (!trip.tripName || trip.tripName === '--') {
        return {
            fullTitle: untitledFallback,
            isRoundTrip: false
        }
    }
    const name = trip.tripName.toLowerCase()
    if (name.includes('one-way') && trip.tripName.includes(' - ')) {
        const [origin, destination] = trip.tripName.split('one-way')[0].trim().split(' - ')
        return {
            fullTitle: `${origin} → ${destination}`,
            isRoundTrip: false
        }
    }
    if (name.includes('return') && trip.tripName.includes(' - ')) {
        const [origin, destination] = trip.tripName.split('return')[0].trim().split(' - ')
        return {
            fullTitle: `${origin} ↔ ${destination}`,
            isRoundTrip: true,
            origin,
            destination
        }
    }
    return {
        fullTitle: trip.tripName,
        isRoundTrip: false
    }
}
